import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Small delay before showing banner
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie_consent', JSON.stringify(onlyNecessary));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const savedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie_consent', JSON.stringify(savedPreferences));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto bg-brand-charcoal rounded-xl shadow-2xl border border-white/10 overflow-hidden">
          {!showPreferences ? (
            // Main Banner
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-brand-gold" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">
                    We use cookies to enhance your browsing experience, analyze site traffic, 
                    and personalize content. By clicking "Accept All", you consent to our use of cookies. 
                    You can manage your preferences or learn more in our{' '}
                    <a href="/privacy" className="text-brand-gold hover:underline">
                      Privacy Policy
                    </a>.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="bg-brand-gold text-brand-charcoal font-semibold px-6 py-2.5 hover:bg-brand-gold-lite transition-colors"
                      data-testid="cookie-accept-all"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={handleRejectAll}
                      className="border border-white/20 text-white font-semibold px-6 py-2.5 hover:bg-white/10 transition-colors"
                      data-testid="cookie-reject-all"
                    >
                      Reject All
                    </button>
                    <button
                      onClick={() => setShowPreferences(true)}
                      className="text-gray-400 font-medium px-6 py-2.5 hover:text-white transition-colors"
                      data-testid="cookie-customize"
                    >
                      Customize
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Preferences Panel
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-xl font-bold text-white">
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Close preferences"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-white mb-1">Necessary Cookies</h4>
                    <p className="text-gray-400 text-sm">
                      Essential for the website to function properly. Cannot be disabled.
                    </p>
                  </div>
                  <div className="w-12 h-6 bg-brand-gold rounded-full relative cursor-not-allowed">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-white mb-1">Analytics Cookies</h4>
                    <p className="text-gray-400 text-sm">
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <button
                    onClick={() => togglePreference('analytics')}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      preferences.analytics ? 'bg-brand-gold' : 'bg-gray-600'
                    }`}
                    data-testid="cookie-toggle-analytics"
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        preferences.analytics ? 'right-1' : 'left-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-white mb-1">Marketing Cookies</h4>
                    <p className="text-gray-400 text-sm">
                      Used to deliver personalized advertisements and track campaigns.
                    </p>
                  </div>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      preferences.marketing ? 'bg-brand-gold' : 'bg-gray-600'
                    }`}
                    data-testid="cookie-toggle-marketing"
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        preferences.marketing ? 'right-1' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="bg-brand-gold text-brand-charcoal font-semibold px-6 py-2.5 hover:bg-brand-gold-lite transition-colors"
                  data-testid="cookie-save-preferences"
                >
                  Save Preferences
                </button>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="border border-white/20 text-white font-semibold px-6 py-2.5 hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
